import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Skeleton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from './AuthContext';
import { getHouses } from './api';
import AdminLayout from './admin/AdminLayout';

function SortableHouseRow({ house, onDeleteClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: house.id });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: isDragging ? '#1e1e1e' : '#161616',
        border: '1px solid',
        borderColor: isDragging ? '#444' : '#222',
        borderRadius: 2,
        px: 2,
        py: 1.5,
        opacity: isDragging ? 0.6 : 1,
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      {/* Drag handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          color: '#555',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          '&:active': { cursor: 'grabbing' },
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {/* Thumbnail */}
      {house.cover_image ? (
        <Box
          component="img"
          src={house.cover_image}
          alt={house.name}
          sx={{
            width: 64,
            height: 64,
            objectFit: 'cover',
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 64,
            height: 64,
            backgroundColor: '#1e1e1e',
            borderRadius: 1,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" color="text.disabled">None</Typography>
        </Box>
      )}

      {/* Name + location */}
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle2" fontWeight={600} noWrap>
          {house.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {house.location || '—'}
        </Typography>
      </Box>

      {/* Actions */}
      <Box display="flex" gap={1} flexShrink={0}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          component={Link}
          to={`/dashboard/house/${house.id}/edit`}
          sx={{ borderColor: '#333', color: '#ccc', '&:hover': { borderColor: '#aaa' } }}
        >
          Edit
        </Button>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDeleteClick(house)}
          sx={{ border: '1px solid #331111', borderRadius: 1 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

function Dashboard() {
  const { authHeaders } = useAuth();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const load = () => {
    setLoading(true);
    getHouses().then(setHouses).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setHouses(prev => {
      const oldIdx = prev.findIndex(h => h.id === active.id);
      const newIdx = prev.findIndex(h => h.id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      fetch('/api/houses/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ house_ids: reordered.map(h => h.id) }),
      });
      return reordered;
    });
  };

  const handleDelete = async () => {
    setDeleting(true);
    await fetch(`/api/houses/${deleteTarget.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    setDeleting(false);
    setDeleteTarget(null);
    load();
  };

  return (
    <AdminLayout>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Homes
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {loading ? '...' : `${houses.length} project${houses.length !== 1 ? 's' : ''} — drag to reorder`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/dashboard/house/new"
          sx={{
            backgroundColor: '#FFA13C',
            color: '#000',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#e8912e' },
          }}
        >
          Add House
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {[1, 2, 3].map(n => (
            <Skeleton key={n} variant="rounded" height={88} sx={{ bgcolor: '#1e1e1e' }} />
          ))}
        </Box>
      ) : houses.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed #333',
            borderRadius: 3,
            py: 10,
            textAlign: 'center',
          }}
        >
          <Typography color="text.secondary" mb={2}>No houses yet.</Typography>
          <Button
            variant="outlined"
            component={Link}
            to="/dashboard/house/new"
            sx={{ borderColor: '#FFA13C', color: '#FFA13C' }}
          >
            Add your first house
          </Button>
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={houses.map(h => h.id)} strategy={verticalListSortingStrategy}>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {houses.map(house => (
                <SortableHouseRow
                  key={house.id}
                  house={house}
                  onDeleteClick={setDeleteTarget}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { backgroundColor: '#161616', border: '1px solid #2a2a2a', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete house?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            "{deleteTarget?.name}" and all its photos will be permanently removed.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#888' }}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}

export default Dashboard;
